/**
 * PendingInvitesList Component
 *
 * Displays and manages pending workspace invites.
 * Allows workspace admins to view, cancel, and resend invites.
 */

import React, { useState, useEffect } from 'react';
import {
  Mail,
  X,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { ConfirmModal } from './ConfirmModal';

interface WorkspaceInvite {
  id: number;
  workspace_id: number;
  inviter_id: number;
  invitee_email: string;
  invitee_user_id: number | null;
  token: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'expired';
  permissions: {
    can_view_analytics: number;
    can_manage_agents: number;
    can_manage_workspace: number;
    can_admin: number;
  };
  expires_at: string;
  responded_at: string | null;
  created_at: string;
  updated_at: string;
  inviter?: {
    id: number;
    name: string;
    email: string;
  };
}

interface PendingInvitesListProps {
  workspaceId: number;
  onInviteCancelled?: () => void;
}

export const PendingInvitesList: React.FC<PendingInvitesListProps> = ({
  workspaceId,
  onInviteCancelled
}) => {
  const [invites, setInvites] = useState<WorkspaceInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [inviteToCancel, setInviteToCancel] = useState<WorkspaceInvite | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadInvites = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Use axios instance from services (handles auth headers automatically)
      const { default: axiosInstance } = await import('../../../services/axiosConfig');
      const response = await axiosInstance.get(
        `/workspaces/${workspaceId}/invites`,
        { params: { status: 'pending' } }
      );

      if (response.data.success && response.data.data) {
        setInvites(response.data.data.invites || response.data.data || []);
      } else {
        setInvites([]);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Erro ao carregar convites';
      setError(errorMessage);
      console.error('Failed to load invites:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInvites();
  }, [workspaceId]);

  const handleCancelInviteClick = (invite: WorkspaceInvite) => {
    setInviteToCancel(invite);
    setShowCancelConfirm(true);
  };

  const handleCancelInvite = async () => {
    if (!inviteToCancel) return;

    setCancellingId(inviteToCancel.id);
    setErrorMessage(null);

    try {
      // Use axios instance from services (handles auth headers automatically)
      const { default: axiosInstance } = await import('../../../services/axiosConfig');
      const response = await axiosInstance.delete(
        `/workspaces/${workspaceId}/invites/${inviteToCancel.id}`
      );

      if (response.data.success) {
        // Remove from list
        setInvites(prev => prev.filter(invite => invite.id !== inviteToCancel.id));

        if (onInviteCancelled) {
          onInviteCancelled();
        }

        setShowCancelConfirm(false);
        setInviteToCancel(null);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Erro ao cancelar convite';
      setErrorMessage(errorMessage);
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusIcon = (status: WorkspaceInvite['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'cancelled':
        return <X className="w-4 h-4 text-[#666666]" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4 text-orange-400" />;
    }
  };

  const getStatusText = (status: WorkspaceInvite['status']) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'accepted':
        return 'Aceito';
      case 'rejected':
        return 'Recusado';
      case 'cancelled':
        return 'Cancelado';
      case 'expired':
        return 'Expirado';
    }
  };

  const getStatusColor = (status: WorkspaceInvite['status']) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-400';
      case 'accepted':
        return 'text-green-400';
      case 'rejected':
        return 'text-red-400';
      case 'cancelled':
        return 'text-[#666666]';
      case 'expired':
        return 'text-orange-400';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getPermissionsList = (permissions: WorkspaceInvite['permissions']): string[] => {
    const list: string[] = [];
    if (permissions.can_admin === 1) return ['Administrador (todas as permissões)'];
    if (permissions.can_view_analytics === 1) list.push('Ver analytics');
    if (permissions.can_manage_agents === 1) list.push('Gerenciar agentes');
    if (permissions.can_manage_workspace === 1) list.push('Gerenciar workspace');
    return list.length > 0 ? list : ['Nenhuma permissão'];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-[#666666]">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Carregando convites...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-red-400 mb-1">Erro ao carregar convites</h4>
            <p className="text-xs text-red-400/80">{error}</p>
            <button
              onClick={loadInvites}
              className="mt-3 text-xs text-red-400 hover:text-red-300 underline"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (invites.length === 0) {
    return (
      <div className="text-center py-12">
        <Mail className="w-12 h-12 text-[#666666] mx-auto mb-3" />
        <h3 className="text-lg font-medium text-white mb-1">Nenhum convite pendente</h3>
        <p className="text-sm text-[#666666]">
          Convites enviados aparecerão aqui
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Error Message */}
      {errorMessage && (
        <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {errorMessage}
        </div>
      )}

      <div className="space-y-3">
        {invites.map((invite) => (
        <div
          key={invite.id}
          className="bg-[#2a2a2a] border border-[#333333] rounded-lg p-4 hover:border-[#444444] transition-colors"
        >
          <div className="flex items-start justify-between gap-4">
            {/* Invite Info */}
            <div className="flex-1 min-w-0">
              {/* Email */}
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-[#F4CC04] flex-shrink-0" />
                <span className="text-white font-medium truncate">{invite.invitee_email}</span>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2 mb-2">
                {getStatusIcon(invite.status)}
                <span className={`text-sm ${getStatusColor(invite.status)}`}>
                  {getStatusText(invite.status)}
                </span>
              </div>

              {/* Permissions */}
              <div className="text-xs text-[#666666] mb-2">
                <span className="font-medium">Permissões:</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {getPermissionsList(invite.permissions).map((perm, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 bg-[#1a1a1a] border border-[#333333] rounded text-[#888888]"
                    >
                      {perm}
                    </span>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="text-xs text-[#666666]">
                <div>Enviado: {formatDate(invite.created_at)}</div>
                <div>Expira: {formatDate(invite.expires_at)}</div>
                {invite.inviter && (
                  <div>Por: {invite.inviter.name}</div>
                )}
              </div>
            </div>

            {/* Actions */}
            {invite.status === 'pending' && (
              <button
                onClick={() => handleCancelInviteClick(invite)}
                disabled={cancellingId === invite.id}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Cancelar convite"
              >
                {cancellingId === invite.id ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Cancelar</span>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>

      {/* Cancel Confirmation Modal */}
      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={() => {
          setShowCancelConfirm(false);
          setInviteToCancel(null);
          setErrorMessage(null);
        }}
        onConfirm={handleCancelInvite}
        title="Cancelar Convite"
        message={`Tem certeza que deseja cancelar o convite para "${inviteToCancel?.invitee_email}"?\n\nO link de convite ficará inválido e será necessário enviar um novo convite.`}
        confirmText="Cancelar Convite"
        cancelText="Voltar"
        type="warning"
        isProcessing={cancellingId === inviteToCancel?.id}
      />
    </>
  );
};

export default PendingInvitesList;
